
import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { PageTitle } from "@/components/ui/PageTitle";
import { PaymentMethodSummary } from "@/components/counting/PaymentMethodSummary";
import { Card } from "@/components/ui/card";
import { usePaymentSummaries } from "@/hooks/usePaymentSummaries";
import { DiscrepancyManager } from "@/components/counting/DiscrepancyManager";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PaymentSummary } from "@/components/counting/types/PaymentTypes";
import { SalaryDeductionsTab } from "@/components/counting/tabs/SalaryDeductionsTab";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Suspense } from "react";

const Counting = () => {
  const [period, setPeriod] = useState("day");
  const [countedAmounts, setCountedAmounts] = useState<Record<string, number>>({});
  const { summaries, isLoading } = usePaymentSummaries(period);
  const [activeTab, setActiveTab] = useState("discrepancies");
  const isMobile = useIsMobile();

  const handleCountedChange = (method: string, value: string) => {
    setCountedAmounts((prev) => ({
      ...prev,
      [method]: value ? parseFloat(value) : undefined,
    }));
  };

  const summariesWithCounted: PaymentSummary[] = (summaries || []).map(summary => ({
    ...summary,
    counted: countedAmounts[summary.method],
  }));

  const tabItems = [
    { value: "discrepancies", label: "Historique de la déclaration des différences" },
    { value: "salary-deductions", label: "Retenues sur salaire" },
  ];

  if (isLoading) {
    return (
      <DashboardLayout userType="owner">
        <div>Chargement...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userType="owner">
      <div className="space-y-6">
        <PageTitle title="Comptage du stock" />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          {isMobile ? (
            <div className="mb-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full justify-between">
                    {tabItems.find(tab => tab.value === activeTab)?.label}
                    <Menu className="h-4 w-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-full">
                  {tabItems.map((tab) => (
                    <DropdownMenuItem
                      key={tab.value}
                      onSelect={() => setActiveTab(tab.value)}
                      className={tab.value === activeTab ? "bg-primary/10 text-primary" : ""}
                    >
                      {tab.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <TabsList className="w-full border-b">
              {tabItems.map((tab) => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className="flex-1 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          )}

          <TabsContent value="discrepancies">
            <Suspense fallback={<div>Chargement...</div>}>
              <DiscrepancyManager />
            </Suspense>
          </TabsContent>

          <TabsContent value="salary-deductions">
            <Suspense fallback={<div>Chargement...</div>}>
              <SalaryDeductionsTab showActions={false} />
            </Suspense>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default Counting;
